// ═══════════════════════════════════════════════════════════
// Tampa Debris Store — Import Log, Reconciliations, FEMA Packages
// ═══════════════════════════════════════════════════════════

import { create } from 'zustand';
import { fetchRecords, queryRecords, createRecord, updateRecord, deleteRecord } from '../api/client';
import { TABLES } from '../api/tables';
import { FIELDS } from '../api/fields';
import { useAuthStore } from './authStore';
import type { ImportRecord, ReconciliationRecord, FEMAPackage, SubcontractorInvoice } from '../types/debris';
import { decodeSubInvoiceMeta } from '../types/debris';

const FIL = FIELDS.debrisImportLog;
const FR = FIELDS.debrisReconciliations;
const FFP = FIELDS.debrisFemaPackages;
const FI = FIELDS.debrisInvoices;

// ── Row Mappers ──────────────────────────────────────────────

function mapImportRecord(r: Record<string, unknown>): ImportRecord {
  return {
    id: r.record_id as number,
    Rid: r.record_id as number,
    incidentId: String(r[FIL.incidentId] ?? ''),
    fileName: String(r[FIL.fileName] ?? ''),
    fileType: (String(r[FIL.fileType] ?? 'generic_csv')) as ImportRecord['fileType'],
    contractorId: String(r[FIL.contractorId] ?? ''),
    recordCount: Number(r[FIL.recordCount]) || 0,
    newRecords: Number(r[FIL.newRecords]) || 0,
    updatedRecords: Number(r[FIL.updatedRecords]) || 0,
    skippedRecords: Number(r[FIL.skippedRecords]) || 0,
    errorCount: Number(r[FIL.errorCount]) || 0,
    errors: (() => { try { return JSON.parse(String(r[FIL.errors] ?? '[]')); } catch { return []; } })(),
    status: (String(r[FIL.status] ?? 'pending')) as ImportRecord['status'],
    importedBy: String(r[FIL.importedBy] ?? ''),
    notes: String(r[FIL.notes] ?? ''),
  };
}

function mapReconciliation(r: Record<string, unknown>): ReconciliationRecord {
  return {
    id: r.record_id as number,
    Rid: r.record_id as number,
    incidentId: String(r[FR.incidentId] ?? ''),
    contractorId: String(r[FR.contractorId] ?? ''),
    invoiceId: String(r[FR.invoiceId] ?? ''),
    periodStart: String(r[FR.periodStart] ?? ''),
    periodEnd: String(r[FR.periodEnd] ?? ''),
    status: (String(r[FR.status] ?? 'draft')) as ReconciliationRecord['status'],
    totalInvoiced: Number(r[FR.totalInvoiced]) || 0,
    totalVerified: Number(r[FR.totalVerified]) || 0,
    totalVariance: Number(r[FR.totalVariance]) || 0,
    variancePercent: Number(r[FR.variancePercent]) || 0,
    lineItems: (() => { try { return JSON.parse(String(r[FR.lineItems] ?? '[]')); } catch { return []; } })(),
    exceptions: (() => { try { return JSON.parse(String(r[FR.exceptions] ?? '[]')); } catch { return []; } })(),
    reviewerNotes: String(r[FR.reviewerNotes] ?? ''),
    approvedAmount: Number(r[FR.approvedAmount]) || 0,
    approvedBy: String(r[FR.approvedBy] ?? ''),
    approvedAt: String(r[FR.approvedAt] ?? ''),
  };
}

function mapFemaPackage(r: Record<string, unknown>): FEMAPackage {
  return {
    id: r.record_id as number,
    Rid: r.record_id as number,
    incidentId: String(r[FFP.incidentId] ?? ''),
    packageName: String(r[FFP.packageName] ?? ''),
    femaDisasterNumber: String(r[FFP.femaDisasterNumber] ?? ''),
    pwReference: String(r[FFP.pwReference] ?? ''),
    dateRangeStart: String(r[FFP.dateRangeStart] ?? ''),
    dateRangeEnd: String(r[FFP.dateRangeEnd] ?? ''),
    contractorIds: (() => { try { return JSON.parse(String(r[FFP.contractorIds] ?? '[]')); } catch { return []; } })(),
    zoneIds: (() => { try { return JSON.parse(String(r[FFP.zoneIds] ?? '[]')); } catch { return []; } })(),
    totalCubicYards: Number(r[FFP.totalCubicYards]) || 0,
    totalCost: Number(r[FFP.totalCost]) || 0,
    status: (String(r[FFP.status] ?? 'draft')) as FEMAPackage['status'],
    version: Number(r[FFP.version]) || 1,
    exportedAt: String(r[FFP.exportedAt] ?? ''),
    femaFeedback: String(r[FFP.femaFeedback] ?? ''),
    notes: String(r[FFP.notes] ?? ''),
  };
}

function mapSubInvoice(r: Record<string, unknown>): SubcontractorInvoice {
  const notes = String(r[FI.reviewerNotes] ?? '');
  const meta = decodeSubInvoiceMeta(notes);
  return {
    id: r.record_id as number,
    Rid: r.record_id as number,
    incidentId: String(r[FI.incidentId] ?? ''),
    primeContractorId: String(r[FI.contractorId] ?? ''),
    subName: meta.subName ?? String(r[FI.invoiceNumber] ?? ''),
    subInvoiceNumber: String(r[FI.invoiceNumber] ?? ''),
    subInvoiceDate: String(r[FI.invoiceDate] ?? ''),
    subAmount: Number(r[FI.totalAmount]) || 0,
    workType: meta.workType ?? '',
    documentId: String(r[FI.paymentRef] ?? ''),
    notes,
  };
}

// ── Payload Builders ─────────────────────────────────────────

function importLogToPayload(data: Record<string, unknown>): Record<string, unknown> {
  const p: Record<string, unknown> = {};
  if (data.incidentId !== undefined) p[FIL.incidentId] = data.incidentId;
  if (data.fileName !== undefined) p[FIL.fileName] = data.fileName;
  if (data.fileType !== undefined) p[FIL.fileType] = data.fileType;
  if (data.contractorId !== undefined) p[FIL.contractorId] = data.contractorId;
  if (data.recordCount !== undefined) p[FIL.recordCount] = data.recordCount;
  if (data.newRecords !== undefined) p[FIL.newRecords] = data.newRecords;
  if (data.updatedRecords !== undefined) p[FIL.updatedRecords] = data.updatedRecords;
  if (data.skippedRecords !== undefined) p[FIL.skippedRecords] = data.skippedRecords;
  if (data.errorCount !== undefined) p[FIL.errorCount] = data.errorCount;
  if (data.errors !== undefined) p[FIL.errors] = typeof data.errors === 'string' ? data.errors : JSON.stringify(data.errors);
  if (data.status !== undefined) p[FIL.status] = data.status;
  if (data.importedBy !== undefined) p[FIL.importedBy] = data.importedBy;
  if (data.notes !== undefined) p[FIL.notes] = data.notes;
  return p;
}

function reconciliationToPayload(data: Record<string, unknown>): Record<string, unknown> {
  const p: Record<string, unknown> = {};
  if (data.incidentId !== undefined) p[FR.incidentId] = data.incidentId;
  if (data.contractorId !== undefined) p[FR.contractorId] = data.contractorId;
  if (data.invoiceId !== undefined) p[FR.invoiceId] = data.invoiceId;
  if (data.periodStart !== undefined) p[FR.periodStart] = data.periodStart;
  if (data.periodEnd !== undefined) p[FR.periodEnd] = data.periodEnd;
  if (data.status !== undefined) p[FR.status] = data.status;
  if (data.totalInvoiced !== undefined) p[FR.totalInvoiced] = data.totalInvoiced;
  if (data.totalVerified !== undefined) p[FR.totalVerified] = data.totalVerified;
  if (data.totalVariance !== undefined) p[FR.totalVariance] = data.totalVariance;
  if (data.variancePercent !== undefined) p[FR.variancePercent] = data.variancePercent;
  if (data.lineItems !== undefined) p[FR.lineItems] = typeof data.lineItems === 'string' ? data.lineItems : JSON.stringify(data.lineItems);
  if (data.exceptions !== undefined) p[FR.exceptions] = typeof data.exceptions === 'string' ? data.exceptions : JSON.stringify(data.exceptions);
  if (data.reviewerNotes !== undefined) p[FR.reviewerNotes] = data.reviewerNotes;
  if (data.approvedAmount !== undefined) p[FR.approvedAmount] = data.approvedAmount;
  if (data.approvedBy !== undefined) p[FR.approvedBy] = data.approvedBy;
  if (data.approvedAt !== undefined) p[FR.approvedAt] = data.approvedAt;
  return p;
}

function femaPackageToPayload(data: Record<string, unknown>): Record<string, unknown> {
  const p: Record<string, unknown> = {};
  if (data.incidentId !== undefined) p[FFP.incidentId] = data.incidentId;
  if (data.packageName !== undefined) p[FFP.packageName] = data.packageName;
  if (data.femaDisasterNumber !== undefined) p[FFP.femaDisasterNumber] = data.femaDisasterNumber;
  if (data.pwReference !== undefined) p[FFP.pwReference] = data.pwReference;
  if (data.dateRangeStart !== undefined) p[FFP.dateRangeStart] = data.dateRangeStart;
  if (data.dateRangeEnd !== undefined) p[FFP.dateRangeEnd] = data.dateRangeEnd;
  if (data.contractorIds !== undefined) p[FFP.contractorIds] = typeof data.contractorIds === 'string' ? data.contractorIds : JSON.stringify(data.contractorIds);
  if (data.zoneIds !== undefined) p[FFP.zoneIds] = typeof data.zoneIds === 'string' ? data.zoneIds : JSON.stringify(data.zoneIds);
  if (data.totalCubicYards !== undefined) p[FFP.totalCubicYards] = data.totalCubicYards;
  if (data.totalCost !== undefined) p[FFP.totalCost] = data.totalCost;
  if (data.status !== undefined) p[FFP.status] = data.status;
  if (data.version !== undefined) p[FFP.version] = data.version;
  if (data.exportedAt !== undefined) p[FFP.exportedAt] = data.exportedAt;
  if (data.femaFeedback !== undefined) p[FFP.femaFeedback] = data.femaFeedback;
  if (data.notes !== undefined) p[FFP.notes] = data.notes;
  return p;
}

// ── Store Interface ──────────────────────────────────────────

interface TampaDebrisState {
  // Import Log
  imports: ImportRecord[];
  loadingImports: boolean;
  loadImports: () => Promise<void>;
  createImportLog: (data: Record<string, unknown>) => Promise<number | null>;
  updateImportLog: (id: number, data: Record<string, unknown>) => Promise<void>;

  // Reconciliations
  reconciliations: ReconciliationRecord[];
  loadingReconciliations: boolean;
  loadReconciliations: () => Promise<void>;
  createReconciliation: (data: Record<string, unknown>) => Promise<number | null>;
  updateReconciliation: (id: number, data: Record<string, unknown>) => Promise<void>;
  deleteReconciliation: (id: number) => Promise<void>;

  // FEMA Packages
  femaPackages: FEMAPackage[];
  loadingFemaPackages: boolean;
  loadFemaPackages: () => Promise<void>;
  createFemaPackage: (data: Record<string, unknown>) => Promise<number | null>;
  updateFemaPackage: (id: number, data: Record<string, unknown>) => Promise<void>;
  deleteFemaPackage: (id: number) => Promise<void>;

  // Sub-contractor Invoices
  subInvoices: SubcontractorInvoice[];
  loadingSubInvoices: boolean;
  loadSubInvoices: () => Promise<void>;
  createSubInvoice: (data: {
    incidentId: string;
    primeContractorId: string;
    subName: string;
    subInvoiceNumber: string;
    subInvoiceDate: string;
    subAmount: number;
    workType: string;
    documentId?: string;
    notes?: string;
  }) => Promise<number | null>;
  updateSubInvoice: (id: number, data: Partial<{
    subName: string; subInvoiceNumber: string; subInvoiceDate: string;
    subAmount: number; workType: string; documentId: string; notes: string;
  }>) => Promise<void>;
  deleteSubInvoice: (id: number) => Promise<void>;
}

// ── Store Implementation ─────────────────────────────────────

export const useTampaDebrisStore = create<TampaDebrisState>((set, get) => ({
  // ── Import Log ────────────────────────────────────────────
  imports: [],
  loadingImports: false,

  loadImports: async () => {
    set({ loadingImports: true });
    try {
      const inc = useAuthStore.getState().activeIncident;
      const incId = inc?.id?.toString() ?? '';
      const incFilter = incId ? [{ field: FIL.incidentId, operator: 'eq' as const, value: incId }] : [];
      const res = await queryRecords<Record<string, unknown>>(TABLES.debrisImportLog, {
        limit: 200,
        sort: { field: 'created_at', direction: 'desc' },
        ...(incFilter.length > 0 ? { filters: incFilter } : {}),
      });
      // GAB filter API is unreliable — also filter client-side
      set({ imports: res.records.map(mapImportRecord).filter(i => !incId || i.incidentId === incId) });
    } catch (e) {
      console.error('[Tampa] loadImports', e);
    } finally {
      set({ loadingImports: false });
    }
  },

  createImportLog: async (data) => {
    try {
      const res = await createRecord<Record<string, unknown>>(TABLES.debrisImportLog, importLogToPayload(data));
      await get().loadImports();
      return (res as any).record_id ?? null;
    } catch (e) {
      console.error('[Tampa] createImportLog', e);
      return null;
    }
  },

  updateImportLog: async (id, data) => {
    try {
      await updateRecord(TABLES.debrisImportLog, id, importLogToPayload(data));
      await get().loadImports();
    } catch (e) {
      console.error('[Tampa] updateImportLog', e);
    }
  },

  // ── Reconciliations ───────────────────────────────────────
  reconciliations: [],
  loadingReconciliations: false,

  loadReconciliations: async () => {
    set({ loadingReconciliations: true });
    try {
      const inc = useAuthStore.getState().activeIncident;
      const incId = inc?.id?.toString() ?? '';
      const incFilter = incId ? [{ field: FR.incidentId, operator: 'eq' as const, value: incId }] : [];
      const res = await queryRecords<Record<string, unknown>>(TABLES.debrisReconciliations, {
        limit: 200,
        sort: { field: 'created_at', direction: 'desc' },
        ...(incFilter.length > 0 ? { filters: incFilter } : {}),
      });
      const mapped = res.records.map(mapReconciliation);
      // GAB filter API is unreliable — also filter client-side
      set({ reconciliations: mapped.filter(r => !incId || r.incidentId === incId) });
    } catch (e) {
      console.error('[Tampa] loadReconciliations', e);
    } finally {
      set({ loadingReconciliations: false });
    }
  },

  createReconciliation: async (data) => {
    try {
      const res = await createRecord<Record<string, unknown>>(TABLES.debrisReconciliations, reconciliationToPayload(data));
      await get().loadReconciliations();
      return (res as any).record_id ?? null;
    } catch (e) {
      console.error('[Tampa] createReconciliation', e);
      return null;
    }
  },

  updateReconciliation: async (id, data) => {
    try {
      await updateRecord(TABLES.debrisReconciliations, id, reconciliationToPayload(data));
      await get().loadReconciliations();
    } catch (e) {
      console.error('[Tampa] updateReconciliation', e);
    }
  },

  deleteReconciliation: async (id) => {
    try {
      await deleteRecord(TABLES.debrisReconciliations, id);
      set({ reconciliations: get().reconciliations.filter(r => r.id !== id) });
    } catch (e) {
      console.error('[Tampa] deleteReconciliation', e);
    }
  },

  // ── FEMA Packages ─────────────────────────────────────────
  femaPackages: [],
  loadingFemaPackages: false,

  loadFemaPackages: async () => {
    set({ loadingFemaPackages: true });
    try {
      const inc = useAuthStore.getState().activeIncident;
      const incId = inc?.id?.toString() ?? '';
      const incFilter = incId ? [{ field: FFP.incidentId, operator: 'eq' as const, value: incId }] : [];
      const res = await queryRecords<Record<string, unknown>>(TABLES.debrisFemaPackages, {
        limit: 200,
        sort: { field: 'created_at', direction: 'desc' },
        ...(incFilter.length > 0 ? { filters: incFilter } : {}),
      });
      // GAB filter API is unreliable — also filter client-side
      set({ femaPackages: res.records.map(mapFemaPackage).filter(p => !incId || p.incidentId === incId) });
    } catch (e) {
      console.error('[Tampa] loadFemaPackages', e);
    } finally {
      set({ loadingFemaPackages: false });
    }
  },

  createFemaPackage: async (data) => {
    try {
      const res = await createRecord<Record<string, unknown>>(TABLES.debrisFemaPackages, femaPackageToPayload(data));
      await get().loadFemaPackages();
      return (res as any).record_id ?? null;
    } catch (e) {
      console.error('[Tampa] createFemaPackage', e);
      return null;
    }
  },

  updateFemaPackage: async (id, data) => {
    try {
      await updateRecord(TABLES.debrisFemaPackages, id, femaPackageToPayload(data));
      await get().loadFemaPackages();
    } catch (e) {
      console.error('[Tampa] updateFemaPackage', e);
    }
  },

  deleteFemaPackage: async (id) => {
    try {
      await deleteRecord(TABLES.debrisFemaPackages, id);
      set({ femaPackages: get().femaPackages.filter(p => p.id !== id) });
    } catch (e) {
      console.error('[Tampa] deleteFemaPackage', e);
    }
  },

  // ── Sub-contractor Invoices ───────────────────────────────────
  subInvoices: [],
  loadingSubInvoices: false,

  loadSubInvoices: async () => {
    set({ loadingSubInvoices: true });
    try {
      const inc = useAuthStore.getState().activeIncident;
      const incId = inc?.id?.toString() ?? '';
      const res = await queryRecords<Record<string, unknown>>(TABLES.debrisInvoices, {
        limit: 500,
        sort: { field: 'created_at', direction: 'desc' },
        filters: [
          ...(incId ? [{ field: FI.incidentId, operator: 'eq' as const, value: incId }] : []),
          { field: FI.status, operator: 'eq' as const, value: 'sub_invoice' },
        ],
      });
      const all = res.records
        .filter((r: any) => String(r[FI.status] ?? '') === 'sub_invoice')
        .filter((r: any) => !incId || String(r[FI.incidentId] ?? '') === incId)
        .map(mapSubInvoice);
      set({ subInvoices: all });
    } catch (e) {
      console.error('[Tampa] loadSubInvoices', e);
    } finally {
      set({ loadingSubInvoices: false });
    }
  },

  createSubInvoice: async (data) => {
    const meta = `|||SUBINVOICE|||${JSON.stringify({ subName: data.subName, workType: data.workType })}|||ENDSUBINVOICE|||`;
    const payload: Record<string, unknown> = {
      [FI.incidentId]: data.incidentId,
      [FI.contractorId]: data.primeContractorId,
      [FI.invoiceNumber]: data.subInvoiceNumber,
      [FI.invoiceDate]: data.subInvoiceDate,
      [FI.totalAmount]: data.subAmount,
      [FI.status]: 'sub_invoice',
      [FI.reviewerNotes]: meta + (data.notes ? '\n' + data.notes : ''),
      [FI.paymentRef]: data.documentId ?? '',
    };
    const res = await createRecord<Record<string, unknown>>(TABLES.debrisInvoices, payload);
    await get().loadSubInvoices();
    return (res as any).record_id ?? null;
  },

  updateSubInvoice: async (id, data) => {
    const existing = get().subInvoices.find(s => s.id === id);
    const currentNotes = existing?.notes ?? '';
    const currentMeta = decodeSubInvoiceMeta(currentNotes);
    const newMeta = { subName: data.subName ?? currentMeta.subName, workType: data.workType ?? currentMeta.workType };
    const metaStr = `|||SUBINVOICE|||${JSON.stringify(newMeta)}|||ENDSUBINVOICE|||`;
    const cleanNotes = currentNotes.replace(/\|\|\|SUBINVOICE\|\|\|[\s\S]*?\|\|\|ENDSUBINVOICE\|\|\|/, '').trim();
    const payload: Record<string, unknown> = {};
    if (data.subInvoiceNumber !== undefined) payload[FI.invoiceNumber] = data.subInvoiceNumber;
    if (data.subInvoiceDate !== undefined) payload[FI.invoiceDate] = data.subInvoiceDate;
    if (data.subAmount !== undefined) payload[FI.totalAmount] = data.subAmount;
    if (data.documentId !== undefined) payload[FI.paymentRef] = data.documentId;
    payload[FI.reviewerNotes] = metaStr + (data.notes ?? cleanNotes ? '\n' + (data.notes ?? cleanNotes) : '');
    await updateRecord(TABLES.debrisInvoices, id, payload);
    await get().loadSubInvoices();
  },

  deleteSubInvoice: async (id) => {
    await deleteRecord(TABLES.debrisInvoices, id);
    set({ subInvoices: get().subInvoices.filter(s => s.id !== id) });
  },
}));
